import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { motion, AnimatePresence } from "framer-motion";
import { ConfigContext } from "../Context/ConfigContext";
import axios from "axios";
import { socket } from "./Options";

const styles = {
    wrapper: {
        height: "100vh",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#fff",
        position: "relative",
    },

    centerScreen: {
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#fff",
    },

    card: {
        background: "#fff",
        padding: "40px 30px",
        borderRadius: "10px",
        textAlign: "center",
        maxWidth: "400px",
        width: "90%",
        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
    },

    logo: {
        width: "200px",
        objectFit: "cover",
        marginBottom: "20px",
    },

    storeName: {
        margin: "0",
        fontSize: "24px",
        fontWeight: "600",
        color: "#333",
    },

    subtitle: {
        color: "#666",
        marginBottom: "20px",
        fontSize: "14px",
    },

    qrBox: {
        margin: "20px 0",
        padding: "20px",
        background: "#f8f9fa",
        borderRadius: "10px",
        display: "inline-block",
        position: "relative",
    },

    upi: {
        fontSize: "14px",
        fontWeight: "500",
        color: "#666",
        marginTop: "15px",
        wordBreak: "break-all",
    },

    amountBox: {
        background: "#dc3545",
        color: "white",
        padding: "12px",
        borderRadius: "8px",
        marginTop: "15px",
        fontSize: "20px",
        fontWeight: "bold",
        position: "relative",
    },

    timerBox: {
        background: "#333",
        color: "white",
        padding: "8px",
        borderRadius: "8px",
        marginTop: "10px",
        fontSize: "14px",
        textAlign: "center",
    },

    timerWarning: {
        background: "#dc3545",
        color: "white",
        padding: "8px",
        borderRadius: "8px",
        marginTop: "10px",
        fontSize: "14px",
        textAlign: "center",
        animation: "pulse 1s infinite",
    },

    errorCard: {
        background: "white",
        padding: "40px",
        borderRadius: "10px",
        textAlign: "center",
        maxWidth: "400px",
        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
    },

    waitingBox: {
        padding: "30px",
        background: "#f8f9fa",
        borderRadius: "10px",
        margin: "20px 0",
    },

    errorMessage: {
        color: "#dc3545",
        fontSize: "18px",
        fontWeight: "bold",
        marginBottom: "10px",
    },

    button: {
        marginTop: "20px",
        padding: "10px 20px",
        background: "#dc3545",
        border: "none",
        borderRadius: "5px",
        color: "white",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "bold",
    },

    menuContainer: {
        position: "fixed",
        top: "20px",
        right: "20px",
        zIndex: 1000,
    },

    menuButton: {
        background: "#fff",
        border: "1px solid #ddd",
        borderRadius: "50%",
        width: "40px",
        height: "40px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        fontSize: "20px",
        fontWeight: "bold",
        color: "#333",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        transition: "all 0.2s",
    },

    dropdown: {
        position: "absolute",
        top: "50px",
        right: "0",
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        minWidth: "150px",
        overflow: "hidden",
        zIndex: 1001,
    },

    menuItem: {
        padding: "12px 20px",
        cursor: "pointer",
        fontSize: "14px",
        color: "#333",
        transition: "background 0.2s",
        borderBottom: "1px solid #eee",
    },

    logoutItem: {
        padding: "12px 20px",
        cursor: "pointer",
        fontSize: "14px",
        color: "#dc3545",
        transition: "background 0.2s",
    }
};

// Add keyframe animation to document
const styleSheet = document.createElement("style");
styleSheet.textContent = `
    @keyframes pulse {
        0% { opacity: 1; }
        50% { opacity: 0.7; }
        100% { opacity: 1; }
    }
`;
document.head.appendChild(styleSheet);

const QrDisplayMode = () => {
    const { apiUrl, apiHeadersJson, Logout } = useContext(ConfigContext);
    const [upiInfo, setUpiInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [amount, setAmount] = useState("");
    const [menuOpen, setMenuOpen] = useState(false);
    const [timer, setTimer] = useState(null);
    const [timeLeft, setTimeLeft] = useState(0);
    const menuRef = useRef(null);
    const timerRef = useRef(null);

    let audioContextInitialized = false;

    const initAudio = () => {
        if (!audioContextInitialized) {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            const audioCtx = new AudioContext();
            audioCtx.resume().then(() => {
                console.log("Audio unlocked");
            });
            audioContextInitialized = true;
        }
    };

    // Unlock audio on first user interaction
    useEffect(() => {
        const events = ['click', 'touchstart', 'keydown'];
        events.forEach(event => {
            document.body.addEventListener(event, initAudio, { once: true });
        });

        return () => {
            events.forEach(event => {
                document.body.removeEventListener(event, initAudio);
            });
        };
    }, []);

    const GetInfo = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${apiUrl}/Masters/GetActiveUpiInfo`, {
                headers: apiHeadersJson
            });

            const { success, data } = response.data;

            if (success && data?.length > 0) {
                setUpiInfo(data[0]);
            }

        } catch (error) {
            console.log('error', error);
        } finally {
            setLoading(false);
        }
    };

    const qrValue = useMemo(() => {
        if (!upiInfo) return "";

        if (amount && amount <= 99) {
            return "Sorry ₹100 or more is required for online UPI payments";
        } else {
            let url = `upi://pay?pa=${upiInfo.upi_id}&pn=${upiInfo.store_name}&cu=INR`;

            if (amount) {
                url += `&am=${amount}`;
            }

            return url;
        }
    }, [upiInfo, amount]);

    // Play notification sound
    const playNotificationSound = () => {
        const audio = new Audio("voices/success.wav");
        audio.play();
    };
    const playClearNotificationSound = () => {
        const audio = new Audio("voices/clear.wav");
        audio.play();
    };

    const errorPlay = () => {
        const audio = new Audio("voices/error.wav");
        audio.play();
    }

    const connectPlay = () => {
        const audio = new Audio("voices/connected_voice.mp3");
        audio.play();
    }

    // Start countdown timer
    const startTimer = (durationInSeconds = 60) => {
        // Clear existing timer
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }

        setTimeLeft(durationInSeconds);

        timerRef.current = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    // Auto clear when timer reaches 0
                    clearInterval(timerRef.current);
                    setAmount("");
                    setTimer(null);
                    playClearNotificationSound();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    // Clear timer
    const clearTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        setTimeLeft(0);
        setTimer(null);
    };

    // Format time as MM:SS
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Handle reload
    const handleReload = () => {
        clearTimer();
        window.location.reload();
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        GetInfo();
    }, []);

    useEffect(() => {
        socket.on("receive_device_message", () => {
            connectPlay();
        });

        return () => {
            socket.off("receive_device_message");
        };

    }, []);



    useEffect(() => {
        socket.on("qr_receive_payment", (data) => {
            console.log("Received payment request:", data);

            if (data?.amount) {
                // Clear previous timer
                clearTimer();

                // Set new amount
                setAmount(data.amount);

                // Play notification sound

                // Start 1 minute timer
                playNotificationSound();
                startTimer(60);
                setTimer(true);
            }
        });

        return () => {
            socket.off("qr_receive_payment");
            clearTimer();
        };
    }, []);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            clearTimer();
        };
    }, []);

    if (loading) {
        return (
            <div style={styles.centerScreen}>
                <h3>Loading...</h3>
            </div>
        );
    }

    if (!upiInfo) {
        return (
            <div style={styles.centerScreen}>
                <div style={styles.errorCard}>
                    <h3 style={styles.errorMessage}>No UPI Found</h3>
                    <p>Please configure UPI details first</p>
                </div>
            </div>
        );
    }

    // if (amount && amount <= 99) {
    //     return (
    //         <div style={styles.centerScreen}>
    //             <motion.div
    //                 style={styles.errorCard}
    //                 initial={{ scale: 0.9 }}
    //                 animate={{ scale: 1 }}
    //             >
    //                 <div style={{ fontSize: "48px", marginBottom: "10px" }}>⚠️</div>
    //                 <h3 style={styles.errorMessage}>Minimum Amount Required: ₹100</h3>
    //                 <p style={{ color: "#666", marginTop: "10px" }}>
    //                     Online UPI payments only allow transactions of ₹100 or more
    //                 </p>
    //                 <p style={{ color: "#dc3545", fontWeight: "bold", marginTop: "10px" }}>
    //                     Current amount: ₹{amount}
    //                 </p>
    //                 <motion.button
    //                     style={styles.button}
    //                     whileHover={{ scale: 1.05 }}
    //                     whileTap={{ scale: 0.95 }}
    //                     onClick={() => {
    //                         setAmount("");
    //                         clearTimer();
    //                     }}
    //                 >
    //                     Try Again
    //                 </motion.button>
    //             </motion.div>
    //         </div>
    //     );
    // }

    return (
        <div style={styles.wrapper}>
            {/* Three dot menu */}
            <div style={styles.menuContainer} ref={menuRef}>
                <motion.div
                    style={styles.menuButton}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setMenuOpen(!menuOpen)}
                >
                    ⋮
                </motion.div>

                <AnimatePresence>
                    {menuOpen && (
                        <motion.div
                            style={styles.dropdown}
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                        >
                            <motion.div
                                style={styles.menuItem}
                                whileHover={{ background: "#f5f5f5" }}
                                onClick={handleReload}
                            >
                                🔄 Reconnect
                            </motion.div>
                            <motion.div
                                style={styles.logoutItem}
                                whileHover={{ background: "#f5f5f5" }}
                                onClick={Logout}
                            >
                                🚪 Logout
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            <motion.div
                style={styles.card}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.3 }}
            >
                {upiInfo.store_logo && (
                    <img
                        src={`${upiInfo.store_logo_url}`}
                        alt="logo"
                        style={styles.logo}
                    />
                )}

                <h1 style={styles.storeName}>{upiInfo.store_name}</h1>
                <p style={styles.subtitle}>Scan & Pay using any UPI App</p>

                <AnimatePresence mode="wait">
                    {amount ? (
                        <motion.div
                            key="qr"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <div style={styles.qrBox}>
                                <QRCodeCanvas value={qrValue} size={200} />
                            </div>

                            <div style={styles.amountBox}>
                                Amount: ₹{amount}
                            </div>

                            {/* Countdown Timer */}
                            {timeLeft > 0 && (
                                <motion.div
                                    initial={{ scale: 0.9, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    style={timeLeft <= 10 ? styles.timerWarning : styles.timerBox}
                                >
                                    ⏱️ Expires in: {formatTime(timeLeft)}
                                    {timeLeft <= 10 && " ⚠️ Hurry up!"}
                                </motion.div>
                            )}
                        </motion.div>
                    ) : (
                        <motion.div
                            key="waiting"
                            style={styles.waitingBox}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <p>Waiting for payment request...</p>
                        </motion.div>
                    )}
                </AnimatePresence>

                <p style={styles.upi}>📍 {upiInfo.upi_id}</p>
            </motion.div>
        </div>
    );
};

export default QrDisplayMode;